import { ReactNode } from "react";

import UIcon from "lib/icons/untitled_ui/UIcon";
import { SubscriptionEnum } from "lib/types/graphQLEnums";

export type StudioType = "sales" | "bid";

type PlanHeader = {
  name: string;
  type: SubscriptionEnum;
  priceInfo: string;
};

type FeatureRow = {
  tooltip?: string;
  feature: string;
  forFree?: ReactNode;
  forBasic?: ReactNode;
  forGrowth?: ReactNode;
  forExpert?: ReactNode;
};

// Section groups features under a header
export type PlanSection = {
  header: string;
  features: FeatureRow[];
};

const included = <UIcon icon="checkOutlined" size={24} color="sysPrimaryDefault" />;
const notIncluded = <UIcon icon="xClose" size={24} color="sysDestructiveDefault" />;

const includedForAllPlans = (studio: StudioType) => {
  const base = {
    forFree: included,
    forGrowth: included,
    forExpert: included,
  };
  return studio === "sales" ? { ...base, forBasic: included } : base;
};

const includedForAllPlansExceptFree = (studio: StudioType) => {
  const base = {
    forFree: notIncluded,
    forGrowth: included,
    forExpert: included,
  };
  return studio === "sales" ? { ...base, forBasic: included } : base;
};

const includedForGrowthExpert = (studio: StudioType) => {
  const base = {
    forFree: notIncluded,
    forGrowth: included,
    forExpert: included,
  };
  return studio === "sales" ? { ...base, forBasic: notIncluded } : base;
};

const includedForExpert = (studio: StudioType) => {
  const base = {
    forFree: notIncluded,
    forGrowth: notIncluded,
    forExpert: included,
  };
  return studio === "sales" ? { ...base, forBasic: notIncluded } : base;
};

const mainIntegrationsPerPlan = (studio: StudioType) => {
  const base = {
    forFree: notIncluded,
    forGrowth: <span>Self-serve</span>,
    forExpert: <span>Supported</span>,
  };
  return studio === "sales" ? { ...base, forBasic: <span>Self-serve</span> } : base;
};

type StudioPlanConfig = {
  headers: PlanHeader[];
  sections: PlanSection[];
  audience: string;
};

export type StudioPlanDetailsConfig = {
  sales: StudioPlanConfig;
  bid: StudioPlanConfig;
};

export const FREE_PRICE_INFO = "£0";
export const CUSTOM_PRICING_INFO = "Custom pricing";
export const BID_GROWTH_CARD_PRICE_INFO = "From £495";
export const BID_GROWTH_HEADER_PRICE_INFO = "From £495 / month";

export const getStudioPlanDetailsConfig = (
  studio: StudioType,
  isAnnual = true,
): StudioPlanDetailsConfig => {
  return {
    sales: {
      headers: [
        { name: "Free", type: SubscriptionEnum.Freemium, priceInfo: FREE_PRICE_INFO },
        {
          name: "Basic",
          type: SubscriptionEnum.Basic,
          priceInfo: isAnnual ? "From £99 / month" : "From £150 / month",
        },
        { name: "Growth", type: SubscriptionEnum.Growth, priceInfo: "From £475 / month" },
        { name: "Expert", type: SubscriptionEnum.Expert, priceInfo: CUSTOM_PRICING_INFO },
      ],
      sections: [
        {
          header: "Market data",
          features: [
            {
              feature: "Open tenders",
              ...includedForAllPlans(studio),
            },
            {
              feature: "Procurement contacts",
              ...includedForAllPlans(studio),
            },
            {
              feature: "Contract awards",
              ...includedForAllPlansExceptFree(studio),
            },
            {
              feature: "Frameworks",
              ...includedForGrowthExpert(studio),
            },
            {
              feature: "Buyer documents",
              tooltip:
                "Quickly find reliable early buying signals and inform your account planning with our database of strategic buyer documents.",
              ...includedForGrowthExpert(studio),
            },
            {
              feature: "Invoiced spend",
              ...includedForGrowthExpert(studio),
            },
            {
              feature: "Decision maker contacts",
              ...includedForGrowthExpert(studio),
            },
            {
              feature: "Exports",
              ...includedForGrowthExpert(studio),
            },
          ],
        },
        {
          header: "Pipeline tools",
          features: [
            {
              feature: "Unlimited saved searches",
              tooltip:
                "Create and save custom views for particular searches using the data filters below.",
              ...includedForAllPlans(studio),
            },
            {
              feature: "Saved lists",
              ...includedForAllPlans(studio),
            },
            {
              feature: "Customisable alerts",
              ...includedForAllPlans(studio),
            },
            {
              feature: "Advanced filtering",
              ...includedForAllPlans(studio),
            },
            {
              feature: "AI signal scores",
              tooltip: "Build your custom feed with industry and supplier keywords and CPV codes.",
              ...includedForAllPlans(studio),
            },
            {
              feature: "AI notice summaries",
              tooltip:
                "Save time with AI-generated summaries that highlight key information such as requirements, contract value and duration.",
              ...includedForAllPlans(studio),
            },
            {
              feature: "AI research",
              tooltip: "Get instant answers to your questions without searching the data yourself",
              ...includedForGrowthExpert(studio),
            },
            {
              feature: "AI outreach",
              tooltip:
                "Let our AI outreach builder build personalised messages to decision makers for you in seconds.",
              ...includedForGrowthExpert(studio),
            },
          ],
        },
        {
          header: "Strategy tools",
          features: [
            {
              feature: "Buyer profiles",
              ...includedForGrowthExpert(studio),
            },
            {
              feature: "Supplier profiles",
              ...includedForGrowthExpert(studio),
            },
            {
              feature: "Framework profiles",
              ...includedForGrowthExpert(studio),
            },
            {
              feature: "Trend over time analysis",
              ...includedForExpert(studio),
            },
            {
              feature: "Buyer rankings",
              ...includedForExpert(studio),
            },
            {
              feature: "Supplier rankings",
              ...includedForExpert(studio),
            },
            {
              feature: "Framework rankings",
              ...includedForExpert(studio),
            },
          ],
        },
        {
          header: "Platform, security & admin",
          features: [
            {
              feature: "Collaboration tools",
              ...includedForAllPlans(studio),
            },
            {
              feature: "Collaborator seat availability",
              ...includedForGrowthExpert(studio),
            },
            {
              feature: "Team utilisation view",
              ...includedForGrowthExpert(studio),
            },
            {
              feature: "Guest access",
              forFree: notIncluded,
              forBasic: notIncluded,
              forGrowth: notIncluded,
              forExpert: "Coming soon",
            },
            {
              feature: "Single sign-on (SSO)",
              ...includedForExpert(studio),
            },
          ],
        },
        {
          header: "Integrations",
          features: [
            {
              feature: "Salesforce",
              ...mainIntegrationsPerPlan(studio),
            },
            {
              feature: "Hubspot",
              ...mainIntegrationsPerPlan(studio),
            },
            {
              feature: "Included",
              ...mainIntegrationsPerPlan(studio),
            },
            {
              feature: "MSTeams",
              ...mainIntegrationsPerPlan(studio),
            },
            {
              feature: "Slack",
              ...mainIntegrationsPerPlan(studio),
            },
            {
              feature: "Custom integrations",
              forFree: notIncluded,
              forBasic: notIncluded,
              forGrowth: "Custom pricing",
              forExpert: "Custom pricing",
            },
          ],
        },
      ],
      audience: "For sales teams",
    },
    bid: {
      headers: [
        { name: "Free", type: SubscriptionEnum.Freemium, priceInfo: FREE_PRICE_INFO },
        { name: "Growth", type: SubscriptionEnum.Growth, priceInfo: BID_GROWTH_HEADER_PRICE_INFO },
        { name: "Expert", type: SubscriptionEnum.Expert, priceInfo: CUSTOM_PRICING_INFO },
      ],
      sections: [
        {
          header: "Track tenders",
          features: [
            {
              feature: "Saved searches",
              tooltip: "Build your custom feeds with industry keywords and CPV codes.",
              ...includedForAllPlans(studio),
            },
            {
              feature: "Customisable alerts",
              ...includedForAllPlans(studio),
            },
            {
              feature: "Advanced filtering",
              ...includedForAllPlans(studio),
            },
            {
              feature: "AI Signal Scores",
              tooltip:
                "Built on our AI-driven proprietary algorithm from your signal settings to show you the most relevant opportunities at a glance. and CPV codes.",
              ...includedForAllPlans(studio),
            },
            {
              feature: "AI notice summaries",
              tooltip:
                "Save time with AI-generated summaries that highlight key information such as requirements, contract value and duration.",
              ...includedForAllPlans(studio),
            },
            {
              feature: "AI tender document summary",
              tooltip:
                "Get the key information you need in seconds. Upload tender documents and have the details extracted into a standard format to make a bid / no-bid decision.",
              ...includedForGrowthExpert(studio),
            },
          ],
        },
        {
          header: "Win bids",
          features: [
            {
              feature: "AI bid / no-bid reports",
              forFree: "3 (Lifetime)",
              forGrowth: "15 per month",
              forExpert: "Unlimited",
            },
            {
              feature: "Bid project collaboration tools",
              forFree: notIncluded,
              forGrowth: "5 active",
              forExpert: "Unlimited",
            },
            {
              feature: "Secure bid content library",
              ...includedForGrowthExpert(studio),
            },
            {
              feature: "AI search",
              tooltip:
                "Search Stotles market data, the internet and your own content library from one place with Stotles AI search",
              ...includedForGrowthExpert(studio),
            },
            {
              feature: "AI response extraction",
              tooltip:
                "No more manually copy and pasting questions from the tender documents into excel. Stotles AI pull each question out and organises them ready for response drafting.",
              ...includedForGrowthExpert(studio),
            },
            {
              feature: "AI drafting co-pilot",
              tooltip:
                "Draft responses faster with Stotles AI co-pilot that can pull from your own content, market data and the internet.",
              ...includedForGrowthExpert(studio),
            },
            {
              feature: "AI response scoring",
              tooltip:
                "Get an idea of how your response will land. Stotles AI evaluates your response against the requirements and best practices.",
              ...includedForGrowthExpert(studio),
            },
          ],
        },
        {
          header: "AI source data",
          features: [
            {
              feature: "Buyers procurement history",
              ...includedForGrowthExpert(studio),
            },
            {
              feature: "Buyer documents",
              ...includedForGrowthExpert(studio),
            },
            {
              feature: "Supplier relationships",
              ...includedForGrowthExpert(studio),
            },
            {
              feature: "Secure bid content library",
              ...includedForGrowthExpert(studio),
            },
            {
              feature: "Web searches",
              ...includedForGrowthExpert(studio),
            },
          ],
        },
        {
          header: "Platform, security & admin",
          features: [
            {
              feature: "Collaboration tools",
              tooltip:
                "Real-time communication across the platforms with the use of comments, assigning, alert and notifications.",
              ...includedForAllPlans(studio),
            },
            {
              feature: "Collaborator seat availability",
              tooltip:
                "Get your whole team working in one place. Give limited access to users who need to view, comment or just generally review content in Stotles.",
              ...includedForGrowthExpert(studio),
            },
            {
              feature: "Project permissions",
              forFree: notIncluded,
              forGrowth: "Coming soon",
              forExpert: "Coming soon",
            },
            {
              feature: "Guest access",
              forFree: notIncluded,
              forGrowth: notIncluded,
              forExpert: "Coming soon",
              tooltip: "Share bids and responses with users outside your organisation.",
            },
            {
              feature: "Single sign-on (SSO)",
              ...includedForExpert(studio),
            },
          ],
        },
        {
          header: "Integrations",
          features: [
            {
              feature: "Salesforce",
              ...includedForGrowthExpert(studio),
            },
            {
              feature: "Hubspot",
              ...includedForGrowthExpert(studio),
            },
            {
              feature: "Slack",
              forFree: notIncluded,
              forGrowth: "Coming soon",
              forExpert: "Coming soon",
            },
            {
              feature: "MSTeams",
              forFree: notIncluded,
              forGrowth: "Coming soon",
              forExpert: "Coming soon",
            },
            {
              feature: "Cloud storage",
              forFree: notIncluded,
              forGrowth: "Coming soon",
              forExpert: "Coming soon",
              tooltip:
                "Integrate your existing Sharepoint or Google Drive files with Stotles content library",
            },
          ],
        },
      ],
      audience: "For bid and business development teams",
    },
  };
};
